import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Dayjs } from 'dayjs';
import { SmartScheduleEvent, SmartSchedulePeriod } from '@/features/smartSchedule/model';
import AgendaPanel from './AgendaPanel';
import EventCard from './EventCard';
import {
    DAY_NAMES,
    buildWeekSlotRows,
    formatSlotTime,
    getEventStart,
    getStatusGroup,
    startOfMondayWeek,
} from './utils';

type WeekViewProps = {
    selectedDay: any;
    now: Dayjs;
    events: SmartScheduleEvent[];
    onSelectedDayChange: (date: any) => void;
    onPeriodChange: (period: SmartSchedulePeriod) => void;
    onOpenEvent: (event: SmartScheduleEvent) => void;
};

const SLOT_MINUTES = 60;
const WEEK_HEADER_HEIGHT = 40;
const DEFAULT_WEEK_CARD_COLUMNS = 3;
const WEEK_TIME_COLUMN_WIDTH = 64;
const MIN_THREE_COLUMN_DAY_WIDTH = 166;
const MIN_TWO_COLUMN_DAY_WIDTH = 108;
const WEEK_CARD_MIN_HEIGHT = 24;
const WEEK_CARD_MAX_HEIGHT = 26;
const WEEK_ROW_PADDING = 3;
const WEEK_CARD_GAP = 2;
const WEEK_CURRENT_ROW_MIN_HEIGHT = 34;

export const formatWeekHourLabel = (minute: number) => {
    const hour = Math.floor(minute / 60) % 24;
    const meridiem = hour < 12 ? '오전' : '오후';
    const displayHour = hour % 12 || 12;
    return `${meridiem} ${displayHour}시`;
};

export const getWeekCardColumns = (tableWidth: number) => {
    if (tableWidth <= 0) return DEFAULT_WEEK_CARD_COLUMNS;
    const dayWidth = (tableWidth - WEEK_TIME_COLUMN_WIDTH) / 7;
    if (dayWidth >= MIN_THREE_COLUMN_DAY_WIDTH) return 3;
    if (dayWidth >= MIN_TWO_COLUMN_DAY_WIDTH) return 2;
    return 1;
};

const WeekView = ({ selectedDay, now, events, onSelectedDayChange, onPeriodChange, onOpenEvent }: WeekViewProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [rowViewportBudget, setRowViewportBudget] = useState(0);
    const [cardColumns, setCardColumns] = useState(DEFAULT_WEEK_CARD_COLUMNS);
    const [showOutsideHours, setShowOutsideHours] = useState(false);
    const weekStart = useMemo(() => startOfMondayWeek(selectedDay), [selectedDay]);
    const days = useMemo(() => Array.from({ length: 7 }, (_, index) => weekStart.add(index, 'day')), [weekStart]);
    const startHour = 6;
    const endHour = 24;
    const startMinute = startHour * 60;
    const endMinute = endHour * 60;
    const { operatingEvents, outsideHoursEvents } = useMemo(() => {
        const inOperatingHours: SmartScheduleEvent[] = [];
        const outsideOperatingHours: SmartScheduleEvent[] = [];
        events.forEach((event) => {
            const start = getEventStart(event);
            const minute = start.hour() * 60 + start.minute();
            if (minute < startMinute) outsideOperatingHours.push(event);
            else inOperatingHours.push(event);
        });
        outsideOperatingHours.sort((left, right) => getEventStart(left).valueOf() - getEventStart(right).valueOf());
        return { operatingEvents: inOperatingHours, outsideHoursEvents: outsideOperatingHours };
    }, [events, startMinute]);
    const slotRows = useMemo(
        () => buildWeekSlotRows(operatingEvents, days, startMinute, endMinute, SLOT_MINUTES),
        [days, endMinute, operatingEvents, startMinute]
    );
    const todayIndex = days.findIndex((date) => date.isSame(now, 'day'));
    const containsToday = todayIndex >= 0;
    const currentMinute = now.hour() * 60 + now.minute();
    const rowLaneCounts = useMemo(
        () => slotRows.map((row) => Math.max(
            1,
            ...row.dayEvents.map((dayEvents) => Math.ceil(dayEvents.length / cardColumns))
        )),
        [cardColumns, slotRows]
    );
    const totalLaneCount = rowLaneCounts.reduce((total, laneCount) => total + laneCount, 0);
    const totalGapCount = rowLaneCounts.reduce((total, laneCount) => total + Math.max(0, laneCount - 1), 0);
    const availableCardsHeight = Math.max(
        0,
        rowViewportBudget
            - WEEK_HEADER_HEIGHT
            - slotRows.length * WEEK_ROW_PADDING
            - totalGapCount * WEEK_CARD_GAP
    );
    const fittedCardHeight = totalLaneCount > 0
        ? Math.floor(availableCardsHeight / totalLaneCount)
        : WEEK_CARD_MAX_HEIGHT;
    const cardHeight = Math.max(
        WEEK_CARD_MIN_HEIGHT,
        Math.min(WEEK_CARD_MAX_HEIGHT, fittedCardHeight || WEEK_CARD_MAX_HEIGHT)
    );
    const cardFontSize = Math.max(10.5, Math.min(11.5, 10.5 + (cardHeight - WEEK_CARD_MIN_HEIGHT) / 2));
    const sessionFontSize = Math.max(9.5, cardFontSize - 1);

    useEffect(() => {
        const scrollElement = scrollRef.current;
        if (!scrollElement) return undefined;

        let frame = 0;
        const measure = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const tableTop = scrollElement.getBoundingClientRect().top;
                const tableWidth = scrollElement.getBoundingClientRect().width || scrollElement.clientWidth;
                const measuredBudget = Math.max(
                    0,
                    Math.floor(window.innerHeight - Math.max(0, tableTop) - 8)
                );
                setRowViewportBudget((current) => current === measuredBudget ? current : measuredBudget);
                const measuredColumns = getWeekCardColumns(tableWidth);
                setCardColumns((current) => current === measuredColumns ? current : measuredColumns);
            });
        };

        measure();
        window.addEventListener('resize', measure);
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(measure);
            observer.observe(scrollElement);
            return () => {
                window.cancelAnimationFrame(frame);
                observer.disconnect();
                window.removeEventListener('resize', measure);
            };
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
        };
    }, [slotRows.length]);

    const openDay = (date: Dayjs) => {
        onSelectedDayChange(date);
        onPeriodChange('day');
    };

    return (
        <section className="smart-schedule__week" aria-label="주간 일정 타임라인">
            {outsideHoursEvents.length > 0 && (
                <button
                    type="button"
                    className="smart-schedule__week-outside-hours"
                    onClick={() => setShowOutsideHours(true)}
                    aria-label={`오전 6시 이전 일정 ${outsideHoursEvents.length}개 전체 보기`}
                >
                    <strong>오전 6시 이전 {outsideHoursEvents.length}개</strong>
                    <span>
                        {outsideHoursEvents.slice(0, 3).map((event) => {
                            const start = getEventStart(event);
                            return `${start.format('ddd HH:mm')} ${event.coachName || '미지정'}·${event.title || '일정'}`;
                        }).join(' / ')}
                    </span>
                    <em>전체 보기</em>
                </button>
            )}
            <div ref={scrollRef} className="smart-schedule__week-scroll" role="region" aria-label="요일별 주간 일정">
                <div
                    className="smart-schedule__week-canvas"
                    style={{ '--week-card-columns': cardColumns } as CSSProperties}
                >
                    <div className="smart-schedule__week-header">
                        <div className="smart-schedule__week-time-heading">시간</div>
                        {days.map((date) => {
                            const isToday = date.isSame(now, 'day');
                            return (
                                <button
                                    key={date.format('YYYY-MM-DD')}
                                    type="button"
                                    className={isToday ? 'is-today' : ''}
                                    aria-label={`${date.format('M월 D일')} 일간 일정 보기`}
                                    aria-current={isToday ? 'date' : undefined}
                                    onClick={() => openDay(date)}
                                >
                                    <span>{DAY_NAMES[date.day()]}</span>
                                    <strong>{date.date()}</strong>
                                    {isToday && <em>오늘</em>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="smart-schedule__week-body" role="table" aria-label="60분 단위로 압축 표시되는 주간 일정표">
                        {slotRows.map((row, slotIndex) => {
                            const isCurrentSlot = containsToday
                                && currentMinute >= row.startMinute
                                && currentMinute < row.endMinute;
                            const laneCount = rowLaneCounts[slotIndex];
                            const fittedRowHeight = WEEK_ROW_PADDING
                                + laneCount * cardHeight
                                + Math.max(0, laneCount - 1) * WEEK_CARD_GAP;
                            const rowHeight = Math.max(
                                fittedRowHeight,
                                isCurrentSlot ? WEEK_CURRENT_ROW_MIN_HEIGHT : 0
                            );
                            const hourLabel = formatWeekHourLabel(row.startMinute);
                            return (
                                <div
                                    key={row.startMinute}
                                    data-week-slot-index={slotIndex}
                                    data-week-lanes={laneCount}
                                    className={`smart-schedule__week-slot-row ${isCurrentSlot ? 'is-current-slot' : ''}`}
                                    role="row"
                                    aria-current={isCurrentSlot ? 'time' : undefined}
                                    aria-label={`${formatSlotTime(row.startMinute)} 시간대${isCurrentSlot ? `, 현재 ${now.format('HH:mm')}` : ''}`}
                                    style={{
                                        height: rowHeight,
                                        '--week-card-height': `${cardHeight}px`,
                                        '--week-card-font-size': `${cardFontSize}px`,
                                        '--week-session-font-size': `${sessionFontSize}px`,
                                    } as CSSProperties}
                                >
                                    <div className="smart-schedule__week-slot-time" role="rowheader">
                                        <strong>{hourLabel}</strong>
                                        {isCurrentSlot && (
                                            <span className="smart-schedule__week-current-time" aria-label={`현재 ${now.format('HH:mm')}`}>
                                                <span>현재</span>
                                                <time dateTime={now.format('HH:mm')}>{now.format('HH:mm')}</time>
                                            </span>
                                        )}
                                    </div>
                                    {days.map((date, dayIndex) => {
                                        const slotEvents = row.dayEvents[dayIndex];
                                        const isToday = dayIndex === todayIndex;
                                        return (
                                            <div
                                                key={date.format('YYYY-MM-DD')}
                                                className={`smart-schedule__week-slot-cell ${isToday ? 'is-today' : ''} ${isToday && isCurrentSlot ? 'is-current-slot' : ''} ${slotEvents.length >= 3 ? 'is-dense' : ''}`}
                                                role="cell"
                                                aria-label={`${date.format('M월 D일')} ${formatSlotTime(row.startMinute)}부터 ${formatSlotTime(row.endMinute)}까지, 일정 ${slotEvents.length}개`}
                                            >
                                                <div
                                                    className="smart-schedule__week-slot-events"
                                                    data-week-columns={cardColumns}
                                                    role="group"
                                                    aria-label={`일정 ${slotEvents.length}개, ${cardColumns}열 배치`}
                                                >
                                                    {slotEvents.map((item) => (
                                                        <EventCard
                                                            key={item.event.id}
                                                            event={item.event}
                                                            variant="week-compact"
                                                            displayTime={formatSlotTime(item.startMinute)}
                                                            continuesFromPreviousDay={getEventStart(item.event).isBefore(date)}
                                                            status={getStatusGroup(item.event, now)}
                                                            onOpen={onOpenEvent}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {showOutsideHours && (
                <AgendaPanel
                    title="오전 6시 이전 일정"
                    events={outsideHoursEvents}
                    now={now}
                    variant="week"
                    onClose={() => setShowOutsideHours(false)}
                    onOpenEvent={onOpenEvent}
                />
            )}
        </section>
    );
};

export default React.memo(WeekView);
